import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getBenchmarks, getIndex } from '../../data/kanoseiIndex';
import { refreshAndWait } from '../../data/portfolio';
import PortfolioTabs from '../portfolio/PortfolioTabs';
import PortfolioHeader from '../portfolio/PortfolioHeader';
import ReviewCountdown from '../portfolio/ReviewCountdown';
import IndexPositionsTable from '../portfolio/IndexPositionsTable';
import RebalanceTable from '../portfolio/RebalanceTable';
import TargetWeights from '../portfolio/TargetWeights';
import IndexRules from '../portfolio/IndexRules';
import SectionSummary from '../portfolio/SectionSummary';
import { FadeIn, Skeleton } from '../portfolio/Loading';
import NavChart from '../portfolio/NavChart';
import BenchmarkChart from '../portfolio/BenchmarkChart';
import ViewToggle from '../portfolio/ViewToggle';
import useThemes from '../portfolio/useThemes';
import useSummaryInputs from '../portfolio/useSummaryInputs';
import { summaryParts } from '../portfolio/sections';
import {
    DEFAULT_TIMEFRAME,
    TIMEFRAMES,
    valueChange,
    windowPerformance,
} from '../portfolio/timeframes';
import { useT } from '../../i18n';

const LOAD_STATE = { LOADING: 'loading', READY: 'ready', ERROR: 'error' };

const INNER_TABS = [
    ['portfolio', 'index.tabs.portfolio'],
    ['rebalance', 'index.tabs.rebalance'],
    ['targets', 'index.tabs.targets'],
    ['rules', 'index.tabs.rules'],
];

export default function KanoseiIndex() {
    const { t, tn, fmt } = useT();
    const [params, setParams] = useSearchParams();
    const [state, setState] = useState(LOAD_STATE.LOADING);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshError, setRefreshError] = useState(null);
    // The same period selector as the portfolio page, driving the same figures.
    // The index is one account, so there is no account selector here.
    const [timeframe, setTimeframe] = useState(DEFAULT_TIMEFRAME);
    const [windowKey, setWindowKey] = useState('all');
    // The chart shows the account's value, or its return against the S&P 500
    // and the NASDAQ. The comparison is fetched the first time it is asked for.
    const [chartMode, setChartMode] = useState('value');
    const [benchmarks, setBenchmarks] = useState(null);
    useEffect(() => {
        if (chartMode !== 'compare' || benchmarks) return undefined;
        let cancelled = false;
        getBenchmarks()
            .then((result) => { if (!cancelled) setBenchmarks(result ?? { series: [] }); })
            .catch(() => { if (!cancelled) setBenchmarks({ series: [] }); });
        return () => { cancelled = true; };
    }, [chartMode, benchmarks]);
    const themes = useThemes();
    const { inputs, setInput } = useSummaryInputs();

    // The inner tab lives in the URL so a particular view can be linked to and
    // survives a reload.
    const tab = INNER_TABS.some(([key]) => key === params.get('view'))
        ? params.get('view')
        : 'portfolio';

    const load = useCallback(async ({ refresh = false, previousAsOf = null } = {}) => {
        if (refresh) setRefreshing(true);
        setRefreshError(null);
        try {
            setData(refresh
                ? await refreshAndWait(getIndex, { previousAsOf })
                : await getIndex());
            setState(LOAD_STATE.READY);
        } catch (err) {
            if (refresh) setRefreshError(err?.message ?? null);
            else {
                setError(err);
                setState(LOAD_STATE.ERROR);
            }
        } finally {
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        getIndex()
            .then((result) => {
                if (cancelled) return;
                setData(result);
                setState(LOAD_STATE.READY);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err);
                setState(LOAD_STATE.ERROR);
            });
        return () => { cancelled = true; };
    }, []);

    const rows = useMemo(() => data?.rows ?? [], [data]);
    // The index account in the shape the section summary reads.
    const parts = useMemo(() => (data ? summaryParts([{
        source: 'ibkr',
        scope: 'index',
        accountId: data.accountId ?? null,
        accountLabel: data.accountLabel ?? 'IBKR',
        totalValue: data.totalValue,
        performance: data.performanceWindows,
    }], { inputs }) : []), [data, inputs]);
    const change = useMemo(
        () => valueChange(data?.navHistory, timeframe),
        [data, timeframe],
    );
    const netDeposits = windowPerformance(data?.performanceWindows, timeframe)?.netDeposits;
    const growthRows = useMemo(() => rows.filter((r) => r.sleeve === 'growth'), [rows]);
    const leavingCount = rows.filter((r) => r.leavingIndex).length;

    return (
        <section className="holdings__sec">
            <div className="wrapper">
                <PortfolioHeader
                    title={t('index.title')}
                    status={data?.asOf && [
                        t('common.asOf', { when: fmt.dateTime(data.asOf) }),
                        data.reconstitution?.asOf
                            && t('index.ranking', { date: fmt.date(data.reconstitution.asOf) }),
                    ].filter(Boolean).join(' · ')}
                    onRefresh={() => load({ refresh: true, previousAsOf: data?.asOf })}
                    refreshing={refreshing}
                />

                <PortfolioTabs />

                {state === LOAD_STATE.LOADING && <Skeleton rows={10} />}

                {state === LOAD_STATE.ERROR && (
                    <p className="holdings__error" role="alert">
                        {error?.message || t('index.error')}
                    </p>
                )}

                {state === LOAD_STATE.READY && data && (
                    <>
                        {data.totalValue && (
                            <div className="holdings__total">
                                <span className="holdings__total_value">
                                    {fmt.number(data.totalValue)}
                                </span>
                                <span className="holdings__total_currency">{data.baseCurrency}</span>
                            </div>
                        )}

                        <SectionSummary
                            parts={parts}
                            windowKey={windowKey}
                            onWindow={setWindowKey}
                            inputs={inputs}
                            onInput={setInput}
                        />

                        {/* Weights are measured against invested capital, so
                            cash sitting in the account moves no band and every
                            position reads as compliant. That is the rule, and
                            it is also how a large undeployed balance stays
                            invisible — so it is stated. */}
                        {data.deployment?.deployedPct !== null
                            && data.deployment?.deployedPct !== undefined
                            && data.deployment.deployedPct < 99 && (
                            <p className="index__deployment">
                                {t('index.deployment', {
                                    pct: data.deployment.deployedPct,
                                    cash: fmt.number(data.deployment.cash, 0),
                                    currency: data.baseCurrency,
                                })}
                            </p>
                        )}

                        {data.sleeve?.entrantCount > 0 && (
                            <p className="index__sleeve">
                                {tn('index.sleeveOver', data.sleeve.entrantCount, {
                                    count: data.sleeve.entrantCount,
                                    names: data.sleeve.assetCount,
                                    target: fmt.number(data.sleeve.assetTargetPct, 1),
                                })}
                            </p>
                        )}

                        <ReviewCountdown
                            schedule={data.schedule}
                            breachCount={data.breachCount}
                            leavingCount={leavingCount}
                        />

                        {refreshError && (
                            <p className="holdings__error" role="alert">
                                {t('holdings.refreshFailed', { reason: refreshError })}
                            </p>
                        )}

                        {data.warnings?.length > 0 && (
                            <Link to="/holdings/notes" className="holdings__notices">
                                {tn('holdings.notices', data.warnings.length)}
                            </Link>
                        )}

                        <div className="holdings__timeframe holdings__chartbar">
                            <ViewToggle
                                ariaLabel={t('timeframe.aria')}
                                options={TIMEFRAMES.map((f) => [f.key, t(f.labelKey)])}
                                value={timeframe}
                                onChange={setTimeframe}
                            />
                            <ViewToggle
                                ariaLabel={t('benchmarks.modeAria')}
                                options={[['value', t('benchmarks.value')], ['compare', t('benchmarks.compare')]]}
                                value={chartMode}
                                onChange={setChartMode}
                            />
                        </div>

                        {chartMode === 'value' ? (
                            <NavChart
                                navHistory={data.navHistory}
                                currency={data.baseCurrency}
                                netDeposits={netDeposits}
                                timeframe={timeframe}
                                windowChange={change}
                            />
                        ) : (
                            benchmarks ? <BenchmarkChart data={benchmarks} timeframe={timeframe} /> : <Skeleton rows={4} />
                        )}

                        <nav className="itabs" aria-label={t('index.tabsAria')}>
                            {INNER_TABS.map(([key, label]) => (
                                <button
                                    key={key}
                                    type="button"
                                    className={`itabs__tab${tab === key ? ' is-active' : ''}`}
                                    onClick={() => setParams(key === 'portfolio' ? {} : { view: key })}
                                >
                                    {t(label)}
                                    {key === 'rebalance' && data.breachCount > 0 && (
                                        <span className="itabs__badge">{data.breachCount}</span>
                                    )}
                                </button>
                            ))}
                        </nav>

                        <FadeIn viewKey={tab}>
                            {tab === 'portfolio' && (
                                <IndexPositionsTable rows={rows} baseCurrency={data.baseCurrency} themes={themes} />
                            )}
                            {tab === 'rebalance' && (
                                <RebalanceTable
                                    rows={rows}
                                    baseCurrency={data.baseCurrency}
                                    bands={data.bands}
                                    hardCapPct={data.hardCapPct}
                                />
                            )}
                            {tab === 'rules' && (
                                <IndexRules reconstitution={data.reconstitution} />
                            )}
                            {tab === 'targets' && (
                                <TargetWeights
                                    reconstitution={data.reconstitution}
                                    schedule={data.schedule}
                                    growthRows={growthRows}
                                />
                            )}
                        </FadeIn>
                    </>
                )}
            </div>
        </section>
    );
}
